import React, { useState, useEffect, useRef, forwardRef } from 'react';

interface ImageWithFallbackProps {
  src: string;
  fallbackSrc?: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
  /** Optional: delay before showing fallback (useful for slow connections) */
  fallbackDelay?: number;
  /** Optional: 'eager' when image is preloaded (e.g. bonus whos-on-image) */
  loading?: 'lazy' | 'eager';
}

/**
 * Reusable image component with automatic fallback handling.
 * 
 * Features:
 * - Automatically switches to fallback image on error
 * - Handles HTTP error codes (429, 404, 500, etc.)
 * - Preloads fallback image for faster switching
 * - Can be used across all games that display images
 */
const ImageWithFallback = forwardRef<HTMLImageElement, ImageWithFallbackProps>(({
  src,
  fallbackSrc,
  alt,
  className,
  style,
  onLoad,
  onError,
  fallbackDelay = 0,
  loading = 'lazy',
}, ref) => {
  const [currentSrc, setCurrentSrc] = useState<string>(src);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);
  const fallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when src changes
  useEffect(() => {
    setCurrentSrc(src);
    setHasError(false);
    setIsLoading(true);
    
    // Clear any pending timeout
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
  }, [src]);

  // Preload fallback image if provided
  useEffect(() => {
    if (fallbackSrc && !hasError) {
      const preloadImg = new Image();
      preloadImg.src = fallbackSrc;
    }
  }, [fallbackSrc, hasError]);

  const handleError = () => {
    // Image failed to load - could be 404, 429, 500, network error, etc.
    if (!hasError && fallbackSrc) {
      console.warn('🖼️ ImageWithFallback: Primary image failed to load, switching to fallback', {
        primaryUrl: src,
        fallbackUrl: fallbackSrc,
        reason: 'onError event triggered'
      });
      // Switch to fallback immediately
      switchToFallback('error');
    } else {
      console.error('🖼️ ImageWithFallback: Image failed to load and no fallback available', {
        primaryUrl: src,
        hasFallback: !!fallbackSrc
      });
      if (onError) {
        onError();
      }
    }
  };

  const switchToFallback = (reason: 'error' | 'timeout' = 'error') => {
    if (fallbackSrc && !hasError) {
      if (fallbackDelay > 0) {
        fallbackTimeoutRef.current = setTimeout(() => {
          console.warn('🖼️ ImageWithFallback: Switching to fallback image (delayed)', {
            primaryUrl: src,
            fallbackUrl: fallbackSrc,
            reason
          });
          setCurrentSrc(fallbackSrc);
          setHasError(true);
          setIsLoading(false);
        }, fallbackDelay);
      } else {
        if (reason === 'timeout') {
          console.warn('🖼️ ImageWithFallback: Primary image timeout (likely 429 rate limit), switching to fallback', {
            primaryUrl: src,
            fallbackUrl: fallbackSrc,
            timeout: '3 seconds'
          });
        }
        setCurrentSrc(fallbackSrc);
        setHasError(true);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
      if (onError) {
        onError();
      }
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    
    // Log successful load - check currentSrc to determine which image loaded
    if (fallbackSrc && currentSrc === fallbackSrc) {
      console.log('🖼️ ImageWithFallback: Fallback image loaded successfully', {
        fallbackUrl: fallbackSrc,
        originalUrl: src
      });
    } else {
      console.log('🖼️ ImageWithFallback: Primary image loaded successfully', {
        url: src,
        hasFallback: !!fallbackSrc
      });
    }
    
    if (onLoad) {
      onLoad();
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
    };
  }, []);

  // Pre-check image availability and handle slow/stuck requests
  useEffect(() => {
    if (src && !hasError) {
      // Set a timeout to detect slow/stuck requests (common with 429 rate limiting)
      // If image doesn't load within reasonable time, switch to fallback
      const timeoutId = setTimeout(() => {
        if (isLoading && !hasError && fallbackSrc) {
          // Image is taking too long - likely rate-limited (429) or network issue
          // Switch to fallback immediately
          switchToFallback('timeout');
        }
      }, 3000); // 3 second timeout for faster fallback on errors
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [src, hasError, fallbackSrc, isLoading]);

  const setRefs = (el: HTMLImageElement | null) => {
    (imgRef as React.MutableRefObject<HTMLImageElement | null>).current = el;
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLImageElement | null>).current = el;
    }
  };

  return (
    <img
      ref={setRefs}
      src={currentSrc}
      alt={alt}
      className={className}
      style={style}
      onLoad={handleLoad}
      onError={handleError}
      loading={loading}
    />
  );
});

ImageWithFallback.displayName = 'ImageWithFallback';

export default ImageWithFallback;
